import { useEffect, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import LinkOffOutlined from "@mui/icons-material/LinkOffOutlined";
import type { CatalogOption, CatalogReference } from "./models";
import {
	ComparisonRequestError,
	getReferenceOptions,
	saveReferenceLink,
} from "./repository";

type Operation = "create" | "link" | "unlink";

export function ReferenceName({ name }: { name: string }) {
	const leadingWhitespace = name.match(/^\s+/)?.[0] ?? "";
	return (
		<>
			<Typography
				component="span"
				variant="inherit"
				sx={{ whiteSpace: "pre-wrap" }}
			>
				{name}
			</Typography>
			{leadingWhitespace.length > 0 && (
				<Typography variant="caption" color="warning.main" display="block">
					{leadingWhitespace.length}{" "}
					{/^ +$/.test(leadingWhitespace)
						? `leading space${leadingWhitespace.length === 1 ? "" : "s"}`
						: "leading whitespace characters"}
				</Typography>
			)}
		</>
	);
}

export function ReferenceDialog({
	item,
	operation,
	snapshotToken,
	onClose,
	onInvalidate,
	suggestedTargetId,
}: {
	item: CatalogReference;
	operation: Operation;
	snapshotToken: string;
	onClose: () => void;
	onInvalidate: (message: string, success: boolean) => void;
	suggestedTargetId?: string;
}) {
	const [options, setOptions] = useState<CatalogOption[]>([]);
	const [selected, setSelected] = useState<CatalogOption | null>(null);
	const [loading, setLoading] = useState(operation === "link");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [stale, setStale] = useState(false);
	const active = useRef<AbortController | null>(null);
	useEffect(() => {
		if (operation !== "link") return;
		const controller = new AbortController();
		getReferenceOptions(item.concept, controller.signal)
			.then((values) => {
				if (controller.signal.aborted) return;
				setOptions(values);
				setSelected(
					values.find(
						(value) => value.id === (suggestedTargetId ?? item.targetId),
					) ?? null,
				);
			})
			.catch((reason) => {
				if (!controller.signal.aborted)
					setError(
						reason instanceof Error
							? reason.message
							: "Could not load MES records.",
					);
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, [item.concept, item.targetId, operation, suggestedTargetId]);
	useEffect(() => () => active.current?.abort(), []);
	async function save() {
		if (active.current || stale || (operation === "link" && !selected)) return;
		const controller = new AbortController();
		active.current = controller;
		setSaving(true);
		setError("");
		try {
			await saveReferenceLink(
				snapshotToken,
				item.name,
				selected?.id ?? null,
				item.targetId,
				operation,
				controller.signal,
			);
			onInvalidate(
				operation === "unlink" ? "Reference link removed." : "Reference saved.",
				true,
			);
			onClose();
		} catch (reason) {
			if (controller.signal.aborted) return;
			const message =
				reason instanceof Error
					? reason.message
					: "The result could not be confirmed. Run the comparison again.";
			setError(message);
			if (!(reason instanceof ComparisonRequestError) || reason.requiresRerun) {
				setStale(true);
				onInvalidate(message, false);
			}
		} finally {
			active.current = null;
			if (!controller.signal.aborted) setSaving(false);
		}
	}
	return (
		<Dialog open fullWidth maxWidth="sm" onClose={saving ? undefined : onClose}>
			<DialogTitle>
				{operation === "create"
					? "Insert in MES and save link"
					: operation === "unlink"
						? "Remove reference link"
						: "Link to an existing MES record"}
			</DialogTitle>
			<DialogContent>
				<Stack gap={2} sx={{ pt: 1 }}>
					{error && <Alert severity="error">{error}</Alert>}
					<div>
						Rollshop: <ReferenceName name={item.name} />
					</div>
					{operation === "link" && (
						<Autocomplete
							options={options}
							value={selected}
							loading={loading}
							disabled={saving || stale}
							getOptionLabel={(option) => `${option.name} (ID ${option.id})`}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							onChange={(_, value) => setSelected(value)}
							renderInput={(params) => (
								<TextField {...params} label="Existing MES record" />
							)}
						/>
					)}
					{operation === "create" && (
						<Alert severity="info">
							Creates a MES catalog record named “{item.name}” using catalog
							defaults, and saves the link. Referencing items can then use its
							ID.
						</Alert>
					)}
					{operation === "link" && (
						<Alert severity="info">
							Saves this name as an alias for the selected MES ID. Future
							comparisons and updates use that ID. Existing MES records keep
							their names.
							{suggestedTargetId && (
								<>
									{" "}
									This link applies to all Rollshop catalog records with this
									name, including capitalization variants. It does not copy
									field values or change the imported Rollshop ID.
								</>
							)}
						</Alert>
					)}
					{operation === "unlink" && (
						<Alert severity="warning">
							Removes the alias for {item.targetName ?? item.targetId}. Future
							comparisons use name matching again.
						</Alert>
					)}
					{(loading || saving) && <LinearProgress />}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button
					onClick={save}
					variant="contained"
					color={operation === "create" ? "success" : "primary"}
					startIcon={
						operation === "create" ? (
							<AddCircleOutline />
						) : operation === "link" ? (
							<LinkOutlined />
						) : (
							<LinkOffOutlined />
						)
					}
					disabled={
						loading || saving || stale || (operation === "link" && !selected)
					}
				>
					{operation === "create"
						? "Insert and link"
						: operation === "unlink"
							? "Remove link"
							: "Save link"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export interface ReferenceSelection {
	item: CatalogReference;
	operation: Operation;
	suggestedTargetId?: string;
}

export function ReferenceActions({
	item,
	disabled,
	stale,
	onSelect,
}: {
	item: CatalogReference;
	disabled: boolean;
	stale: boolean;
	onSelect: (selection: ReferenceSelection) => void;
}) {
	return (
		<>
			<Tooltip
				title={
					stale
						? "Run comparison again."
						: (item.createUnavailableReason ??
							"Insert this name in the MES catalog and save its link")
				}
			>
				<span>
					<Button
						size="small"
						variant="contained"
						color="success"
						startIcon={<AddCircleOutline />}
						disabled={disabled || stale || !item.canCreate}
						onClick={() => onSelect({ item, operation: "create" })}
					>
						Insert in MES
					</Button>
				</span>
			</Tooltip>
			<Button
				size="small"
				variant="contained"
				startIcon={<LinkOutlined />}
				disabled={disabled || stale}
				onClick={() => onSelect({ item, operation: "link" })}
			>
				{item.targetId ? "Change link" : "Link existing MES"}
			</Button>
			{item.targetId && (
				<Button
					size="small"
					variant="outlined"
					startIcon={<LinkOffOutlined />}
					disabled={disabled || stale}
					onClick={() => onSelect({ item, operation: "unlink" })}
				>
					Remove link
				</Button>
			)}
		</>
	);
}

export function ReferenceEvidence({
	item,
	detailed = false,
}: {
	item: CatalogReference;
	detailed?: boolean;
}) {
	return (
		<Stack gap={0.5}>
			{detailed && /^\s/.test(item.name) && (
				<Alert severity="info">
					This name starts with whitespace. Names with different leading
					whitespace are listed separately, even when their visible text is the
					same.
				</Alert>
			)}
			{item.missingSourceCatalog && (
				<Typography variant="body2">
					{detailed
						? `Name used in other Rollshop tables but absent from the Rollshop ${item.catalogName} catalog.`
						: `Absent from Rollshop ${item.catalogName} catalog.`}
				</Typography>
			)}
			<Typography
				variant="body2"
				color={item.targetName ? "success.main" : "text.primary"}
			>
				{item.targetName
					? `${item.targetId ? "Linked: " : "MES name match: "}${item.targetName}${item.targetId ? ` (ID ${item.targetId})` : ""}`
					: item.targetId
						? `Linked MES record is missing (ID ${item.targetId}).`
						: "No unique MES match."}
			</Typography>
			{detailed ? (
				<>
					<Typography variant="body2" sx={{ mt: 1 }}>
						Find these records in the corresponding Rollshop screen using the
						identifiers below. Counts are per field; a record can use the same
						reference in more than one field.
					</Typography>
					<Table size="small" aria-label="Rollshop reference usage">
						<TableHead>
							<TableRow>
								<TableCell>Rollshop catalog / table</TableCell>
								<TableCell>Reference field</TableCell>
								<TableCell>Records to find in Rollshop</TableCell>
								<TableCell align="right">Total</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{item.usages.map((usage) => (
								<TableRow
									key={usage.location}
									sx={{ "& td": { verticalAlign: "top" } }}
								>
									<TableCell>
										<Typography variant="body2">{usage.catalogName}</Typography>
										<Typography variant="caption" color="text.secondary">
											dbo.{usage.location.split(".")[0]}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2">{usage.fieldName}</Typography>
										<Typography variant="caption" color="text.secondary">
											{usage.location}
										</Typography>
									</TableCell>
									<TableCell sx={{ minWidth: 280 }}>
										<Stack component="ul" gap={0.5} sx={{ m: 0, pl: 2 }}>
											{(usage.recordSamples ?? []).map((record, index) => (
												<Typography
													component="li"
													variant="body2"
													key={`${index}:${record}`}
												>
													{record}
												</Typography>
											))}
										</Stack>
										{(usage.recordSamples?.length ?? 0) === 0 ? (
											<Typography variant="body2" color="text.secondary">
												Record identifiers unavailable.
											</Typography>
										) : usage.recordSamples.length < usage.count ? (
											<Typography variant="caption" color="text.secondary">
												Showing {usage.recordSamples.length} of {usage.count}{" "}
												records.
											</Typography>
										) : null}
									</TableCell>
									<TableCell align="right">{usage.count}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
					{item.usages.length === 0 && (
						<Typography variant="body2" color="text.secondary">
							No referencing Rollshop records were found for this name.
						</Typography>
					)}
				</>
			) : (
				item.usages.length > 0 && (
					<Typography variant="caption" color="text.secondary">
						Used in:{" "}
						{item.usages
							.map(
								(usage) =>
									`${usage.catalogName} — ${usage.location} (${usage.count})`,
							)
							.join(", ")}
					</Typography>
				)
			)}
		</Stack>
	);
}
